import React, { useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import ReviewForm from './ReviewForm';
import ReviewModal from './ReviewModal';

const IndieGameItem = ({ game }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const viewTracked = useRef(false);

  // Track view when component mounts (only once)
  useEffect(() => {
    if (!viewTracked.current && game._id) {
      trackView();
      viewTracked.current = true;
    }
  }, [game._id]);

  const trackView = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/analytics/track/view', {
        gameId: game._id,
        trafficSource: 'homepage'
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch (error) {
      // Silently fail - don't disrupt user experience
      console.debug('Analytics tracking failed:', error);
    }
  };

  const trackStoreClick = async (platform) => {
    try {
      await axios.post('/api/analytics/track/store-link', {
        gameId: game._id,
        platform: platform
      });
    } catch (error) {
      console.debug('Store link tracking failed:', error);
    }
  };

  const trackMediaView = async (mediaType) => {
    try {
      await axios.post('/api/analytics/track/media', {
        gameId: game._id,
        mediaType: mediaType
      });
    } catch (error) {
      console.debug('Media tracking failed:', error);
    }
  };

  // Helper function to ensure URL has proper protocol
  const ensureProtocol = (url) => {
    if (!url) return '#';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  const handleReviewSuccess = () => {
    setShowReviewForm(false);
    setEditingReview(null);
  };

  const handleEditClick = (review) => {
    setEditingReview(review);
    setShowReviewForm(true);
  };

  const renderStarRating = (rating) => {
    if (!rating || rating === 0) return null;
    
    return (
      <div className="average-rating">
        <div className="star-rating-display">
          {[1, 2, 3, 4, 5].map((star) => (
            <span
              key={star}
              className={`star ${star <= Math.round(rating) ? 'filled' : 'empty'}`}
            >
              ★
            </span>
          ))}
        </div>
        <span className="rating-text">
          {rating.toFixed(1)} ({game.totalReviews || 0} {game.totalReviews === 1 ? 'review' : 'reviews'})
        </span>
      </div>
    );
  };

  // Helper function to get media URL (handles both Cloudinary and legacy GridFS)
  const getMediaUrl = (mediaItem) => {
    // If it has a url property (Cloudinary), use it directly
    if (mediaItem.url) {
      return mediaItem.url;
    }
    // Otherwise, it's a GridFS fileId (legacy), use the media endpoint
    if (mediaItem.fileId) {
      return `/api/media/${mediaItem.fileId}`;
    }
    return null;
  };

  return (
    <>
      <div 
        className="card" 
        style={{ 
          cursor: 'pointer',
          padding: 0,
          overflow: 'hidden',
          position: 'relative',
          height: '400px',
          width: '100%',
          minWidth: 0,
          borderRadius: '12px',
          boxSizing: 'border-box'
        }}
        onClick={() => navigate(`/indie-games/${game._id}`)}
      >
        {/* Background Image */}
        {game.media && game.media.length > 0 ? (
          (() => {
            const firstMedia = game.media[0];
            const mediaUrl = getMediaUrl(firstMedia);
            const isVideo = firstMedia.fileType && firstMedia.fileType.startsWith('video/');
            
            return (
              <>
                {isVideo ? (
                  <video 
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      pointerEvents: 'none',
                      zIndex: 0
                    }}
                    preload="metadata"
                    muted
                  >
                    <source src={mediaUrl} type={firstMedia.fileType || 'video/mp4'} />
                  </video>
                ) : (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${mediaUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    zIndex: 0
                  }} />
                )}
              </>
            );
          })()
        ) : (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '16px',
            fontWeight: '600',
            zIndex: 0
          }}>
            No Image
          </div>
        )}

        {/* Dark gradient overlay for text readability */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '60%',
          background: 'linear-gradient(to top, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.7) 50%, transparent 100%)',
          pointerEvents: 'none',
          zIndex: 0
        }} />

        {/* Media count badge */}
        {game.media && game.media.length > 1 && (
          <div style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'rgba(0, 0, 0, 0.85)',
            color: 'white',
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            backdropFilter: 'blur(8px)',
            zIndex: 2
          }}>
            📸 {game.media.length}
          </div>
        )}

        {/* Developer badge */}
        {game.userId && (
          <div style={{
            position: 'absolute',
            top: '16px',
            left: '16px',
            background: 'rgba(0, 0, 0, 0.85)',
            color: 'white',
            padding: '8px 14px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            backdropFilter: 'blur(8px)',
            zIndex: 2
          }}>
            <span style={{ fontSize: '14px' }}>👤</span>
            {game.userId.username}
          </div>
        )}

        {/* Description overlay in the middle */}
        {game.description && game.description.trim() !== '' && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'calc(100% - 48px)',
            maxWidth: '400px',
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(10px)',
            padding: '16px 20px',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            zIndex: 1
          }}>
            <p style={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '14px',
              lineHeight: '1.6',
              margin: 0,
              textAlign: 'center',
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {game.description}
            </p>
          </div>
        )}

        {/* Content overlay at bottom */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '24px',
          zIndex: 1
        }}>
          {/* Title */}
          <h3 style={{
            color: 'white',
            fontSize: '28px',
            fontWeight: '700',
            marginBottom: '12px',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.8)',
            lineHeight: '1.2'
          }}>
            {game.name}
          </h3>

          {/* Price Badge */}
          <div style={{
            display: 'inline-block',
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            padding: '8px 16px',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: '600',
            color: 'white',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
            marginBottom: '8px'
          }}>
            {game.price}
          </div>

          {/* Reviews Display */}
          {game.averageRating > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              padding: '6px 12px',
              borderRadius: '20px',
              fontSize: '13px',
              color: 'white'
            }}>
              <div style={{ display: 'flex', gap: '2px' }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    style={{
                      color: star <= Math.round(game.averageRating) ? '#FFD700' : 'rgba(255, 255, 255, 0.3)',
                      fontSize: '14px'
                    }}
                  >
                    ★
                  </span>
                ))}
              </div>
              <span style={{ fontWeight: '600' }}>
                {game.averageRating.toFixed(1)} ({game.totalReviews || 0})
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Hidden sections for backwards compatibility */}
      <div style={{ display: 'none' }}>
        <div style={{ marginBottom: '16px' }}>
          <h3 className="card-title">
            {game.name}
          </h3>
          <p className="card-description">
            {game.description}
          </p>
        </div>

        {/* Hashtags Section */}
        {game.hashtags && game.hashtags.length > 0 && (
          <div style={{ marginBottom: '16px', display: 'none' }}>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px'
            }}>
              {game.hashtags.map((tag, index) => (
                <span
                  key={index}
                  style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    background: 'linear-gradient(135deg, var(--btn-secondary), var(--btn-secondary-hover))',
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 8px rgba(118, 75, 162, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.location.href = `/indie-games?hashtag=${tag}`;
                  }}
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Price Section - Hidden */}
        <div style={{ display: 'none' }}>
          <div style={{
            fontSize: '12px',
            color: 'var(--text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '8px'
          }}>
            Price
          </div>
          <div style={{
            fontSize: '24px',
            color: 'var(--btn-success)',
            fontWeight: '700'
          }}>
            {game.price}
          </div>
        </div>

        {/* Store Links Section - Hidden */}
        <div style={{ marginBottom: '16px', display: 'none' }}>
          <h4 style={{
            color: 'var(--text-primary)',
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Available on
          </h4>
          <div className="card-links">
            {game.storeLinks && game.storeLinks.length > 0 ? (
              game.storeLinks.map((link, index) => (
                <a
                  key={index}
                  href={ensureProtocol(link.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="card-link-badge"
                  style={{
                    background: 'linear-gradient(135deg, var(--btn-secondary), var(--btn-secondary-hover))',
                    boxShadow: '0 2px 8px rgba(118, 75, 162, 0.3)',
                    color: 'white'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    trackStoreClick(link.platform);
                  }}
                >
                  {link.platform}
                </a>
              ))
            ) : (
              <span style={{
                color: 'var(--text-secondary)',
                fontSize: '14px',
                fontStyle: 'italic'
              }}>
                No store links added
              </span>
            )}
          </div>
        </div>
        </div>

        {/* Card Actions Section - Hidden */}
        <div className="card-actions" style={{ display: 'none' }}>
          {game.userId && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <p style={{
                fontSize: '13px',
                color: 'var(--text-secondary)',
                margin: 0
              }}>
                Developed by <span style={{ fontWeight: '600', color: 'var(--text-accent)' }}>{game.userId.username}</span>
              </p>
              <span className="badge badge-approved" style={{ fontSize: '11px' }}>
                Active
              </span>
            </div>
          )}

          {/* Rating Display */}
          {renderStarRating(game.averageRating)}

          {/* Review Actions */}
          <div className="review-actions-container">
            {user ? (
              <button
                className="btn btn-secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowReviewForm(!showReviewForm);
                  setShowReviewModal(false);
                  setEditingReview(null);
                }}
              >
                {showReviewForm ? '✖ Cancel' : '✍️ Write Review'}
              </button>
            ) : (
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                Log in to write a review
              </p>
            )}
            
            <button
              className="btn btn-primary"
              onClick={(e) => {
                e.stopPropagation();
                setShowReviewModal(true);
                setShowReviewForm(false);
                setEditingReview(null);
              }}
            >
              {`👁️ See Reviews (${game.totalReviews || 0})`}
            </button>
          </div>
        </div>

      {/* Review Form - Hidden initially */}
      {showReviewForm && user && (
        <div style={{ marginTop: '16px' }}>
          <ReviewForm
            targetType="IndieGame"
            targetId={game._id}
            existingReview={editingReview}
            onSuccess={handleReviewSuccess}
            onCancel={() => {
              setShowReviewForm(false);
              setEditingReview(null);
            }}
          />
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && (
        <ReviewModal
          show={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          targetType="IndieGame"
          targetId={game._id}
          currentUserId={user?._id}
          onEditClick={handleEditClick}
        />
      )}
    </>
  );
};

export default IndieGameItem;
